import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, ExternalLink, TrendingUp } from 'lucide-react';

const NewsSection = () => {
  const news = [
    {
      title: "Lancement du New Deal Technologique",
      date: "24 février 2025",
      category: "Technologie",
      summary: "Le Président lance une nouvelle stratégie pour faire du Sénégal un leader de l'économie numérique en Afrique.",
      image: "/images/president_portrait_6.jpeg",
      tags: ["Innovation", "Numérique", "Développement"],
      priority: "high"
    },
    {
      title: "Réalisations dans le monde rural après un an de pouvoir",
      date: "Janvier 2025",
      category: "Agriculture",
      summary: "Bilan des accomplissements du Président dans le secteur agricole et rural sénégalais.",
      image: "/images/senegal_palais_2.jpeg",
      tags: ["Agriculture", "Rural", "Développement"],
      priority: "medium"
    },
    {
      title: "Le Sénégal devient pays producteur de pétrole",
      date: "2024",
      category: "Économie",
      summary: "Le Sénégal produit ses premiers barils de pétrole et intègre le cercle des pays producteurs d'hydrocarbures.",
      image: "/images/president_portrait_3.jpeg",
      tags: ["Pétrole", "Économie", "Souveraineté"],
      priority: "high"
    },
    {
      title: "Vision Sénégal 2050 : Cap sur un partenariat gagnant",
      date: "Octobre 2024",
      category: "International",
      summary: "À Paris, le Président déroule son offensive économique devant les chefs d'entreprises français.",
      image: "/images/president_portrait_1.jpeg",
      tags: ["Vision 2050", "Partenariat", "France"],
      priority: "medium"
    },
    {
      title: "Bilan de l'An 1 au pouvoir",
      date: "Avril 2024",
      category: "Gouvernance",
      summary: "Première année de présidence : lutte contre la corruption, baisse du coût de la vie, souveraineté économique.",
      image: "/images/senegal_palais_4.jpeg",
      tags: ["Bilan", "Gouvernance", "Réformes"],
      priority: "high"
    },
    {
      title: "Que retenir des 100 premiers jours",
      date: "Juillet 2024",
      category: "Gouvernance",
      summary: "Bilan des trois premiers mois de la présidence de Bassirou Diomaye Faye.",
      image: "/images/president_portrait_2.jpeg",
      tags: ["100 jours", "Bilan", "Réformes"],
      priority: "medium"
    }
  ];

  const categories = ["Toutes", "Économie", "Gouvernance", "Agriculture", "Technologie", "International"];
  
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-accent text-white';
      case 'medium': return 'bg-secondary text-black';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Économie': return 'text-primary bg-primary/10';
      case 'Gouvernance': return 'text-secondary bg-secondary/10';
      case 'Agriculture': return 'text-accent bg-accent/10';
      case 'Technologie': return 'text-primary bg-primary/10';
      case 'International': return 'text-secondary bg-secondary/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  return (
    <section id="actualites" className="py-20 bg-gradient-card">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* En-tête de section */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
              Actualités
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Suivez les dernières actualités et réalisations du Président Bassirou Diomaye Faye 
              et de son gouvernement.
            </p>
          </div>

          {/* Filtres par catégorie */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((category, index) => (
              <Button
                key={index}
                variant={index === 0 ? "default" : "outline"}
                size="sm"
                className="transition-all duration-300"
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Actualité principale */}
          <div className="mb-12">
            <Card className="shadow-elegant overflow-hidden">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="aspect-video lg:aspect-square overflow-hidden">
                  <img 
                    src={news[0].image}
                    alt={news[0].title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge className={getPriorityColor(news[0].priority)}>
                      À la une
                    </Badge>
                    <Badge variant="outline" className={getCategoryColor(news[0].category)}>
                      {news[0].category}
                    </Badge>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-primary mb-4">
                    {news[0].title}
                  </h3>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    {news[0].summary}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{news[0].date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>Lecture 3 min</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {news[0].tags.map((tag, tagIndex) => (
                      <Badge key={tagIndex} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <Button className="w-fit">
                    Lire l'article complet
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </Card>
          </div>

          {/* Autres actualités */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {news.slice(1).map((article, index) => (
              <Card key={index} className="shadow-card hover:shadow-elegant transition-all duration-300 transform hover:-translate-y-1">
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline" className={getCategoryColor(article.category)}>
                      {article.category}
                    </Badge>
                    {article.priority === 'high' && (
                      <Badge className="bg-accent text-white text-xs">
                        Important
                      </Badge>
                    )}
                  </div>
                  <h4 className="text-lg font-semibold text-primary mb-3 line-clamp-2">
                    {article.title}
                  </h4>
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
                    {article.summary}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
                    <Calendar className="w-3 h-3" />
                    <span>{article.date}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-4">
                    {article.tags.slice(0, 2).map((tag, tagIndex) => (
                      <Badge key={tagIndex} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" className="w-full">
                    Lire plus
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Statistiques des actualités */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Card className="text-center shadow-card">
              <CardContent className="p-6">
                <TrendingUp className="w-8 h-8 mx-auto mb-3 text-primary" />
                <div className="text-2xl font-bold text-primary mb-1">{news.length}</div>
                <div className="text-sm text-muted-foreground">Articles récents</div>
              </CardContent>
            </Card>
            <Card className="text-center shadow-card">
              <CardContent className="p-6">
                <div className="w-8 h-8 mx-auto mb-3 bg-secondary/20 rounded-full flex items-center justify-center">
                  <span className="text-secondary font-bold">5</span>
                </div>
                <div className="text-2xl font-bold text-secondary mb-1">Catégories</div>
                <div className="text-sm text-muted-foreground">Thématiques</div>
              </CardContent>
            </Card>
            <Card className="text-center shadow-card">
              <CardContent className="p-6">
                <div className="w-8 h-8 mx-auto mb-3 bg-accent/20 rounded-full flex items-center justify-center">
                  <span className="text-accent font-bold">📈</span>
                </div>
                <div className="text-2xl font-bold text-accent mb-1">Suivi</div>
                <div className="text-sm text-muted-foreground">Continu</div>
              </CardContent>
            </Card>
            <Card className="text-center shadow-card">
              <CardContent className="p-6">
                <div className="w-8 h-8 mx-auto mb-3 bg-primary/20 rounded-full flex items-center justify-center">
                  <span className="text-primary font-bold">🇸🇳</span>
                </div>
                <div className="text-2xl font-bold text-primary mb-1">National</div>
                <div className="text-sm text-muted-foreground">Portée</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsSection;